<template lang="pug">
.sub-resource-table
  .header
    h2 Sub resources
    BriqButton(variant="secondary" ref="createSubResourceBtn" @click="openSubResourceCreatorModal" data-test-id="create-sub-resource-open-modal") Create sub resource
  BriqAlert(v-if="showSubToNormalMessage" variant="info") You removed some sub resource(s). The removed resource(s) will change to a normal resource, inheriting some information and settings from this group.
  BriqAlert(v-if="showNormalToSubMessage" variant="info") You added some sub resource(s). The added resource(s) will change to sub resource and lose some of its own information and settings.
  BriqDataTable.subresources-data-table-wrapper(
    :data="dataModel"
    :allowEditForItem="x => !x.justCreated"
    :allowDeleteForItem="x => x.justCreated"
    :rowsAreClickable="false"
    @edit="editResource"
    @delete="removeResource"
    data-test-id="sub-resources-table-holder"
    )
    template(slot="actions" slot-scope="{ item }")
      BriqCheckboxStandalone(
        :value="belongsToCurrentResourceGroup(item)" 
        :disabled="!isResourceAvailableForCheck(item)"
        @input="toggleChildResource(item, ...arguments)"
        )
  SubResourceCreatorModal(
    ref="SubResourceCreatorModal"
    :show="showSubResourceCreatorModal"
    @cancel="showSubResourceCreatorModal = false"
    @createSubResource="handleNewSubResource")
</template>

<script>
import { any, propEq, reject, clone } from 'ramda'
import SubResourceCreatorModal from './SubResourceCreatorModal.vue'
import ResourceType from '@v/inventory/resources/edit/ResourceType.js'
import BriqDataTable from '@v/components/BriqDataTable.vue'

export default {
  name: 'SubResourcesTable',
  components: {
    SubResourceCreatorModal,
    BriqDataTable,
  },
  props: {
    childResources: { type: Array, required: true },
    resources: { type: Array, required: true },
    resourceKey: String,
    resource: { type: Object, required: true },
  },
  data() {
    return {
      headers: [
        { text: '', value: 'actions', sortable: false },
        { text: 'Resource', value: 'Description' },
        { text: 'Active', value: 'ShowOnScheduler' },
        { text: 'Capacity', value: 'Capacity' },
        { text: 'Team Size', value: 'TeamSize' },
        { text: 'Timeslot Duration', value: 'TimeslotMinutes' },
        { text: 'Type', value: 'ResourceType' },
      ],
      showSubResourceCreatorModal: false,
      newlyAddedSubResources: [],
      subToNormal: [],
      normalToSub: [],
      originalChildResources: clone(this.childResources).map(resource => resource.Key),
    }
  },
  computed: {
    dataModel() {
      return {
        headers: [
          { field: 'actions', label: '', sortable: false },
          { field: 'Description', label: 'Resource' },
          { field: 'ShowOnScheduler', label: 'Active' },
          { field: 'Capacity', label: 'Capacity' },
          { field: 'TeamSize', label: 'Team Size' },
          { field: 'TimeslotMinutes', label: 'Timeslot Duration' },
          { field: 'ResourceType', label: 'Type' },
        ],
        items: this.filteredResources,
      }
    },
    filteredResources() {
      const currentSubresources = this.resources.filter(resource => {
        return (
          resource.Key !== this.resourceKey &&
          resource.ResourceType !== ResourceType.ResourceGroup &&
          (resource.ResourceType === ResourceType.Normal ||
            resource.ParentResourceKey === this.resourceKey)
        )
      })

      return [...clone(currentSubresources), ...clone(this.newlyAddedSubResources)]
    },
    showSubToNormalMessage() {
      return this.subToNormal.length > 0
    },
    showNormalToSubMessage() {
      return this.normalToSub.length > 0
    },
  },
  methods: {
    openSubResourceCreatorModal() {
      this.showSubResourceCreatorModal = true
    },
    editResource({ item }) {
      this.$window.location.href = `/settings/application/resources/${item.Key}/edit`
    },
    removeResource(resource) {
      this.newlyAddedSubResources = reject(
        propEq('TempKey', resource.TempKey),
        this.newlyAddedSubResources,
      )
      this.$emit('removeNewlyAddedChildResource', resource)
    },
    belongsToCurrentResourceGroup(resource) {
      return resource.Key ? any(propEq('Key', resource.Key), this.childResources) : true
    },
    isResourceAvailableForCheck(resource) {
      if (resource.justCreated) return true
      if (resource.ResourceType === ResourceType.Normal) return true
      if (
        resource.ResourceType === ResourceType.SubResource &&
        resource.ParentResourceKey === this.resourceKey
      ) {
        return true
      }

      return false
    },
    toggleChildResource(item, value) {
      if (this.resources.map(resource => resource.Key).includes(item.Key)) {
        if (this.originalChildResources.includes(item.Key)) {
          if (!value) {
            this.subToNormal.push(item.Key)
          } else {
            this.subToNormal = this.subToNormal.filter(key => !(key === item.Key))
          }
        } else {
          if (value) {
            this.normalToSub.push(item.Key)
          } else {
            this.normalToSub = this.normalToSub.filter(key => !(key === item.Key))
          }
        }
      }
      const selectedResource = {
        checked: value,
        resource: {
          Key: item.Key,
          Abbreviation: item.Abbreviation,
          Color: item.Color,
          Icon: item.Icon,
          ShowOnScheduler: item.ShowOnScheduler,
          TempKey: item.TempKey,
        },
      }
      if (item.justCreated) selectedResource.resource.Name = item.Description
      this.$emit('toggleChildResource', selectedResource)
    },
    handleNewSubResource(newSubResourceData) {
      const newSubResourceForTable = {
        Description: newSubResourceData.name,
        Active: 'Yes',
        Capacity: this.resource.Capacity,
        TeamSize: this.resource.TeamSize,
        TimeslotMinutes: this.resource.TimeslotMinutes,
        ResourceType: ResourceType.SubResource,
        Abbreviation: newSubResourceData.abbreviation,
        Color: newSubResourceData.color,
        Icon: null,
        Key: null,
        TempKey: newSubResourceData.tempKey,
        ShowOnScheduler: newSubResourceData.showOnScheduler,
        Priority: newSubResourceData.priority,
        justCreated: true,
      }
      this.newlyAddedSubResources.push(newSubResourceForTable)

      const newSubResourceForAddingInChildResources = {
        Name: newSubResourceData.name,
        Abbreviation: newSubResourceData.abbreviation,
        Color: newSubResourceData.color,
        ShowOnScheduler: newSubResourceData.showOnScheduler,
        Priority: newSubResourceData.priority,
        TempKey: newSubResourceData.tempKey,
      }
      this.childResources.push(newSubResourceForAddingInChildResources)
      this.showSubResourceCreatorModal = false
    },
  },
}
</script>

<style lang="stylus" scoped>

.sub-resource-table {
  .application--wrap {
    min-height: 1vh !important;
  }
}

.header
  display flex
  justify-content space-between
  margin-bottom 20px

.briq-alert
  margin-bottom 16px
</style>

<style lang="stylus">
#app
  .v-application--wrap
    min-height 1vh

.subresources-data-table-wrapper .briq-data-table, .subresources-data-table-wrapper .briq-data-table th
  font-size 14px
</style>
